/**
 * OfflineBanner — sticky OFFLINE strip. While offline the app is READ-ONLY: cached pages render,
 * every write is blocked in lib/api.js and the user must retry explicitly once back online.
 * HTML-only + Tailwind (no lucide) so it renders even when lazy chunks failed to load offline.
 */
import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useOnline, relativeTimeFrom } from "@/lib/useOnline";
import { formatRelative } from "@/utils/formatters";
import { useI18n } from "@/context/I18nContext";

function useBlockedWriteToast(t) {
  useEffect(() => {
    const onBlocked = () => toast.warning(t("offline.write_blocked"), { id: "f2n-offline-write" });
    const onQuota = () => toast.warning(t("offline.quota"), { id: "f2n-cache-quota" });
    const onSw = (e) => { if (e.data?.type === "F2N_CACHE_QUOTA") onQuota(); };
    window.addEventListener("f2n:offline-write-blocked", onBlocked);
    navigator.serviceWorker?.addEventListener?.("message", onSw);
    return () => {
      window.removeEventListener("f2n:offline-write-blocked", onBlocked);
      navigator.serviceWorker?.removeEventListener?.("message", onSw);
    };
  }, [t]);
}

export function OfflineBanner() {
  const { online, serverDown, lastChangedAt } = useOnline();
  const { t, lang } = useI18n();
  const up = online && !serverDown;
  const [showRecovered, setShowRecovered] = useState(false);
  const prevUp = useRef(up);
  useBlockedWriteToast(t);
  const [, tick] = useState(0);
  useEffect(() => {
    if (online) return;
    const iv = setInterval(() => tick((n) => n + 1), 30000);   // refresh "went offline … ago"
    return () => clearInterval(iv);
  }, [online]);

  useEffect(() => {
    document.documentElement.classList.toggle("f2n-offline", !online);
  }, [online]);

  useEffect(() => {
    const wasDown = !prevUp.current;
    prevUp.current = up;
    if (up && wasDown) {
      setShowRecovered(true);
      const tm = setTimeout(() => setShowRecovered(false), 8000);
      return () => clearTimeout(tm);
    }
  }, [up]);

  const base = "sticky top-0 z-[1000] w-full text-center py-1.5 px-3 text-[11px] font-black shadow-lg";
  if (!online) {
    return (
      <div data-testid="offline-banner" role="status" aria-live="polite"
           className={`${base} bg-amber-500 text-slate-900 border-b border-amber-600`}>
        <span data-testid="offline-banner-copy">
          {t("offline.banner")} · {t("offline.since")} {formatRelative(lastChangedAt, { lang })}
        </span>
      </div>
    );
  }
  if (serverDown) {
    return (
      <div data-testid="server-down-banner" role="status" aria-live="polite" className={`${base} bg-stone-800 text-white`}>
        {t("offline.server_down")}
      </div>
    );
  }
  if (showRecovered) {
    return (
      <div data-testid="online-banner" role="status" aria-live="polite"
           className={`${base} bg-emerald-500 text-slate-900 border-b border-emerald-600`}>
        {t("offline.back_online")}
      </div>
    );
  }
  return null;
}

export function CachedBadge({ updatedAt, className = "", "data-testid": testId }) {
  const rel = updatedAt ? relativeTimeFrom(updatedAt) : "cached";
  return (
    <span data-testid={testId || "cached-badge"}
          className={`inline-flex items-center gap-1 rounded-full bg-amber-500/20 text-amber-200
                      text-[9px] font-black uppercase tracking-wide px-2 py-0.5 ${className}`}
          title="Data served from local cache — may be out of date.">
      <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-300"/>
      Cached · {rel}
    </span>
  );
}

export function OfflineEmpty({ title = "Not available offline",
                               message = "This information needs an internet connection. It will refresh automatically when you're back online.",
                               className = "" }) {
  return (
    <div data-testid="offline-empty" className={`rounded-2xl bg-white/5 border border-white/10 p-4 text-center ${className}`}>
      <div className="text-[24px]" aria-hidden>📴</div>
      <div className="mt-1 text-[13px] font-black text-white">{title}</div>
      <div className="mt-1 text-[11px] text-white/60 leading-snug">{message}</div>
    </div>
  );
}
